import React, { useState, useEffect } from "react";
import { Provider } from "react-native-paper";
import Header from "../../components/Header";
import { useAuthUser } from "../../contexts/useAuthUser";
import { doc, setDoc, getDoc, collection, query, where, getDocs, deleteField } from "firebase/firestore";
import { firestore } from "../../services/firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  Content,
  ViewText,
  ViewTextInput,
  ViewDescription,
  ViewButton,
  ViewPlan,
  ViewHelper,
  VerticalScroll,
  ViewModal,
  ViewRow
} from "./style";
import {
  Container,
  SmallText,
  StandardText,
} from "../../config/theme/globalStyles";
import BrandInput from "../../components/BrandInput";
import Button from "../../components/Button";
import THEME from "../../config/theme";
import { AlertBox } from "../../components/AlertBox";
import { nameValidator, cellphoneValidator, removeSansSerif } from "../../utils/validators";
import {
  isManualGrant,
  isPurchaseLinkedProductEntry,
  isManualStripeCourseEntry,
  isManualStripePlanAccess,
  hasActiveStripeSubscriptionForPlan,
} from "../../utils/accessUtils";
import { hexToRgba } from "../../utils/colorUtils";
import { SafeAreaView, ActivityIndicator, Modal, View, StyleSheet, TouchableOpacity, ScrollView, Text, Pressable } from "react-native";
import { GuruSSO } from "../../services/guru";
import Constants from 'expo-constants';
import DataTable from 'react-data-table-component';
import { Calendar } from 'react-native-calendars';
import { Feather } from '@expo/vector-icons';
import { useCustomTheme } from "../../contexts/useCustomTheme";
import { ADMIN_COLORS, ADMIN_FONT } from "../../config/theme/adminColors";
import AdminInput from "../../components/AdminInput";
import AdminAlert from "../../components/AdminAlert";
import AdminDatePicker from "../../components/AdminDatePicker";
import { SectionCard, Field, FieldLabel, Badge } from "../AdminPanel/Products/formStyles";

// Converts a date string to ISO 'YYYY-MM-DD' so it can seed AdminDatePicker and be
// handed to handleDateConfirm (which does `new Date(date)`). Accepts an already-ISO
// string or the pt-BR 'dd/mm/yyyy' produced by toLocaleDateString; returns undefined
// otherwise. Presentation adapter only — never changes the Firestore write logic.
const toISODate = (value) => {
  if (!value) return undefined;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  // pt-BR 'd/m/yyyy' ou 'dd/mm/yyyy' (toLocaleDateString com month/day 'numeric' pode ter 1 dígito)
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value);
  if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  return undefined;
};

// Data de hoje em ISO 'YYYY-MM-DD' no fuso LOCAL (não usa toISOString p/ evitar off-by-one perto da meia-noite).
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const functions = getFunctions();

export function EditProfile({ navigation, userId, editUserAccess }) {
  const {
    user: authUserDoc,
    userId: currentUserId,
    isLogged,
    isLoadingUser,
    refetchUser,
  } = useAuthUser();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [errorCellphone, setErrorCellphone] = useState("");
  const [errorName, setErrorName] = useState("");
  const [message, setMessage] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isCalendarVisible, setCalendarVisible] = useState(false);
  const [dayStatus, setDayStatus] = useState(null); // null | 'ok' | 'error'
  const [newExpirationDate, setNewExpirationDate] = useState(null);
  // Inline feedback banner for the admin "Editar acesso" mode (AdminAlert).
  const [adminFeedback, setAdminFeedback] = useState(null);
  const [stripeSubscriptions, setStripeSubscriptions] = useState([]);

  const { customColor, customFont } = useCustomTheme();


  useEffect(() => {
    const fetchProducts = async () => {
      // Produtos Guru, Pagarme ou IAP (compras In-App da Apple Store / Google Play)
      const productsCollection = collection(firestore, 'products');
      const q = query(productsCollection, where("marketplace", "in", ["PAGARME", "Guru Digital", "IAP"]));
      const querySnapshot = await getDocs(q);

      const legacyProducts = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        productId: doc.data().productId || doc.id,
        selected: false,
      }));
  
      // Cursos Stripe
      const coursesCollection = collection(firestore, 'courses');
      const coursesSnapshot = await getDocs(coursesCollection);
  
      const stripeCourses = coursesSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          marketplace: "STRIPE",
          productId: data.productId || data.priceId || doc.id,
          name: data.title,
          type: "product", // curso Stripe = venda única → Tipo "Produto"
          selected: false,
        };
      });

      // Planos Stripe (uma entrada por frequência configurada no Firestore)
      const plansSnapshot = await getDocs(collection(firestore, 'plans'));
      const frequencyKeyById = { 1: 'monthly', 2: 'quarterly', 3: 'semiannual', 4: 'yearly' };
      let frequencies = [];
      const planDocs = [];
      plansSnapshot.docs.forEach(planDoc => {
        const data = planDoc.data();
        if (data.isPlanFrequency === true) {
          if (!frequencies.length && Array.isArray(data.planFrequency)) {
            frequencies = data.planFrequency;
          }
        } else {
          // Plano arquivado SEGUE no catálogo: arquivar tira o plano de venda
          // (Plans/index.js:29), mas não pode esconder quem já assina o plano
          // antigo — descartá-lo aqui fazia assinante com subscription ATIVA na
          // Stripe aparecer como "Sem acesso"/"Expirado" (ticket suporte).
          planDocs.push({ id: planDoc.id, data, archived: data.archived === true });
        }
      });
      const stripePlans = [];
      planDocs.forEach(({ id, data, archived }) => {
        frequencies.forEach(freq => {
          const key = frequencyKeyById[freq?.id];
          if (!key) return;
          const productId = data?.productIds?.[key];
          if (!productId) return;
          stripePlans.push({
            marketplace: "STRIPE",
            productId,
            name: `${data.title || data.name || id} | ${freq.frequency || key}`,
            type: "plan", // plano Stripe = assinatura → Tipo "Plano"
            archivedPlan: archived,
            selected: false,
          });
        });
      });

      // Combinar todos
      setProducts([...legacyProducts, ...stripeCourses, ...stripePlans]);
    };
  
    if (userId) {
      fetchProducts();
    }
  }, [userId]);
  

  const options = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  };

  const customStyles = {
    rows: {
      style: {
        color: ADMIN_COLORS.ink,
        fontFamily: THEME.FONTFAMILY.LIGHT,
        fontSize: THEME.FONTSIZE.EXTRASMALL
      },
    },
    headCells: {
      style: {
        color: ADMIN_COLORS.ink,
        fontFamily: THEME.FONTFAMILY.MEDIUM,
        fontSize: THEME.FONTSIZE.EXTRASMALL
      },
    },
  };

  const columns = [
    {
      name: 'Produto',
      selector: row => row.name,
      sortable: true,
    },
    {
      name: 'Marketplace',
      selector: row => row.marketplace,
      sortable: true,
    },
    {
      name: 'Acesso',
      selector: row => {
        if (row.hasAccess) {
          if (row.originalExpirationDate === null || row) {
            return 'Acesso concedido';
          } else if (row.originalExpirationDate > new Date()) {
            return 'Acesso concedido';
          } else {
            return 'Sem acesso';
          }
        } else {
          return 'Sem acesso';
        }
      },
      sortable: true,
    },
    {
      name: 'Validade de acesso',
      cell: row => (
        row.canEditValidity ? (
          <>
            <SmallText color="black">{row.expirationDate || '-'}</SmallText>
            <TouchableOpacity onPress={() => handleDateEdit(row.productId, row.expirationDate)}>
              <Feather name="edit-3" size={24} color="black" />
            </TouchableOpacity>
          </>
        ) : (
          <SmallText color="black">{row.expirationDate || '-'}</SmallText>
        )
      ),
    },
    {
      name: 'Edição',
      cell: row =>
        row.canRemoveFromPanel ? (
          <Button
            onPress={() => handleRemoveAccess(row.productId)}
            title={"Remover acesso"}
            fontFamily={THEME.FONTFAMILY.LIGHT}
            fontSize={THEME.FONTSIZE.EXTRASMALL}
            colorbutton={ADMIN_COLORS.danger}
            colortitle={ADMIN_COLORS.ink}
          />
        ) : !row.hasAccess ? (
          <Button
            onPress={() => handleAddAccess(row.productId)}
            title={"Add acesso manual"}
            fontFamily={THEME.FONTFAMILY.LIGHT}
            fontSize={THEME.FONTSIZE.EXTRASMALL}
            colorbutton={"#3FC380E6"}
            colortitle="#ffffff"
          />
        ) : null,
    },
  ];

  const renderArrow = (direction) => {
    return (
      <SmallText color="black">{direction === 'left' ? '<' : '>'}</SmallText>
    );
  };

  const getUserById = async (uid) => {
    if (!uid) return;
    const docRef = doc(firestore, "users", uid);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      setUser({
        id: uid,
        ...docSnap.data(),
      });
      const subsSnap = await getDocs(collection(firestore, "users", uid, "subscriptions"));
      setStripeSubscriptions(subsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } else {
      setStripeSubscriptions([]);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (userId) {
      getUserById(userId);
    }
  }, [userId]);

  const { Nome_Completo, Email, Celular, CPF, Doc, productIds: userProductIds } = user || {};

  const showAlert = (message) => {
    setVisibleAlert(true);
    setMessage(message);
    // In admin mode we surface feedback inline via AdminAlert. showAlert is only ever
    // called for error/blocking messages here; save-success overrides this below.
    if (editUserAccess) setAdminFeedback({ variant: "error", message });
  };

  const hideAlert = (status) => {
    setVisibleAlert(status);
  };

  const validation = () => {
    let error = false;
    if (!editUserAccess) {
      const nameError = nameValidator(Nome_Completo);
      const cellphoneError = cellphoneValidator(Celular);

      if (nameError || cellphoneError) {
        error = true;
        setErrorName(nameError);
        setErrorCellphone(cellphoneError);
        setLoadingSave(false);
      }
      return error;
    }
  };

  // Sem `userId` no route a tela edita o PRÓPRIO perfil: o documento já veio do
  // boot (AuthUserProvider), então só semeamos o formulário. Com `userId` (admin
  // editando um aluno) a leitura específica acima continua valendo.
  useEffect(() => {
    if (userId || isLoadingUser) return;

    if (isLogged && authUserDoc) {
      setUser({ id: currentUserId, ...authUserDoc });
    }
    setIsLoading(false);
  }, [userId, isLoadingUser, isLogged, authUserDoc, currentUserId]);


  if (isLoading) {
    return (
      <ActivityIndicator
        style={{
          flex: 1,
          backgroundColor: THEME.COLORS.BACKGROUND_ABOUT,
          justifyContent: "center",
        }}
        color={THEME.COLORS.PRIMARY_800}
      />
    );
  }

  async function sendToCustomerPortal() {
    const functionRef = httpsCallable(functions, "ext-firestore-stripe-payments-createPortalLink");
    setLoadingPlan(true);
    try {
      const { data } = await functionRef({
        returnUrl: window.location.origin,
        locale: "auto",
      });
      window.location.assign(data.url);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingPlan(false);
    }
  }

  async function sendToCustomerPortalOfGuru() {
    setLoadingPlan(true);
    try {
      await GuruSSO();
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingPlan(false);
    }
  }

  const handleSave = () => {
    if (!validation()) {
      setLoadingSave(true);
      const userRef = doc(firestore, "users", user.id);
      setDoc(
        userRef,
        {
          Nome_Completo,
          Celular,
        },
        { merge: true }
      )
        .then(() => {
          // Editando o próprio perfil: atualiza o contexto para o resto do app
          // (header, drawer) não seguir com o dado antigo.
          if (!userId) refetchUser();
          showAlert("Concluído! Seus dados foram salvos com sucesso!");
          setAdminFeedback({ variant: "success", message: "Dados do aluno salvos com sucesso!" });
        })
        .catch((error) => {
          console.error(error);
          setAdminFeedback({ variant: "error", message: "Não foi possível salvar. Tente novamente." });
        })
        .finally(() => {
          setLoadingSave(false);
        });
    }
  };

  const purchaseManagedAlert = (marketplace, isStripePlan) => {
    if (isStripePlan) {
      return "Este plano é gerenciado pela Stripe. Altere ou cancele pelo dashboard da Stripe.";
    }
    if (marketplace === "PAGARME") {
      return "Este acesso veio do Pagar.me. Gerencie cancelamentos e validade pelo dashboard Pagar.me.";
    }
    if (marketplace === "IAP") {
      return "Este acesso é uma compra In-App e é gerenciado pela Apple Store / Google Play.";
    }
    return "Este acesso está vinculado a uma compra (Guru). Gerencie pelo dashboard da Guru";
  };

  const handleRemoveAccess = async (productId) => {
    const product = products.find(p => p.productId === productId);
    const marketplace = product?.marketplace;
    const isStripe = marketplace === 'STRIPE';
    const isSubscription = isStripe && product?.type === 'plan';
    const isIAP = marketplace === 'IAP';

    if (isIAP) {
      showAlert(purchaseManagedAlert("IAP", false));
      return;
    }

    if (isSubscription) {
      if (
        user?.plan !== productId ||
        !isManualStripePlanAccess(user, productId, stripeSubscriptions)
      ) {
        showAlert(purchaseManagedAlert("STRIPE", true));
        return;
      }
      try {
        await setDoc(
          doc(firestore, 'users', user.id),
          { plan: "0", planTitle: "", planSource: deleteField() },
          { merge: true }
        );
        location.reload();
      } catch (error) {
        console.error("Erro ao remover plano:", error);
        showAlert("Não foi possível fazer essa operação.");
      }
      return;
    }

    if (isStripe) {
      const course = (user.courses || []).find(c => c.productId === productId);
      if (!course || !isManualStripeCourseEntry(course)) {
        showAlert(purchaseManagedAlert("STRIPE", false));
        return;
      }
      try {
        const updatedCourses = (user.courses || []).filter(
          c => !(c.productId === productId && isManualStripeCourseEntry(c))
        );
        await setDoc(doc(firestore, 'users', user.id), { courses: updatedCourses }, { merge: true });
        location.reload();
      } catch (error) {
        console.error("Erro ao remover curso Stripe:", error);
        showAlert("Não foi possível fazer essa operação.");
      }
      return;
    }

    const manualEntries = (user.productIds || []).filter(
      p => p.productId === productId && isManualGrant(p)
    );
    if (manualEntries.length === 0) {
      showAlert(purchaseManagedAlert(marketplace, false));
      return;
    }

    try {
      const updatedUserProducts = (user.productIds || []).filter(
        prod => !(prod.productId === productId && isManualGrant(prod))
      );
      await setDoc(doc(firestore, 'users', user.id), { productIds: updatedUserProducts }, { merge: true });
      location.reload();
    } catch (error) {
      console.error("Erro ao remover acesso:", error);
      showAlert("Não foi possível fazer essa operação.");
    }
  };
  

  const handleAddAccess = (productId) => {
    setSelectedProduct(productId);
    setNewExpirationDate(todayISO()); // padrão: dia atual já selecionado
    setDayStatus(null);
    setCalendarVisible(true);
  };

  const handleDateEdit = (productId, expirationDate) => {
    setSelectedProduct(productId);
    setNewExpirationDate(toISODate(expirationDate) || todayISO());
    setDayStatus(null);
    setCalendarVisible(true);
  };

  const handleDateConfirm = async (date) => {
    setCalendarVisible(false);

    // `date` chega como 'YYYY-MM-DD' (AdminDatePicker). Monta uma data LOCAL às 23:59
    // do dia escolhido, direto pelas partes — sem parse UTC + getTimezoneOffset
    // (que causava a data trocada na tabela).
    const iso = toISODate(date);
    if (!iso) return;
    const [y, mo, d] = iso.split('-').map(Number);
    const adjustedDate = new Date(y, mo - 1, d, 23, 59, 0, 0);

    const userRef = doc(firestore, "users", user.id);

    const selectedProductData = products.find(p => p.productId === selectedProduct);
    const marketplace = selectedProductData?.marketplace;
    const isStripe = marketplace === 'STRIPE';
    // Mesma regra do handleRemoveAccess: plano Stripe = STRIPE + type 'plan'.
    const isSubscription = isStripe && selectedProductData?.type === 'plan';

    if (isSubscription) {
      if (hasActiveStripeSubscriptionForPlan(stripeSubscriptions, selectedProduct)) {
        showAlert(purchaseManagedAlert("STRIPE", true));
        return;
      }
      const planName = products.find(p => p.productId === selectedProduct)?.name || "";
      try {
        await setDoc(
          userRef,
          { plan: selectedProduct, planTitle: planName, planSource: "manual" },
          { merge: true }
        );
        location.reload();
      } catch (error) {
        console.error("Erro ao atualizar plano:", error);
      }
      return;
    }

    if (isStripe) {
      // Acesso a curso Stripe
      const updatedCourses = user.courses ? [...user.courses] : [];
      const courseIndex = updatedCourses.findIndex(c => c.productId === selectedProduct);
  
      if (courseIndex >= 0) {
        updatedCourses[courseIndex] = {
          ...updatedCourses[courseIndex],
          dueDate: adjustedDate,
          source: 'manual',
        };
      } else {
        updatedCourses.push({ productId: selectedProduct, dueDate: adjustedDate, source: 'manual' });
      }
  
      try {
        await setDoc(userRef, { courses: updatedCourses }, { merge: true });
        location.reload();
      } catch (error) {
        console.error("Erro ao atualizar curso Stripe:", error);
      }
  
    } else {
      // Acesso a produto Guru/Pagarme. Pode haver registros duplicados do mesmo
      // produto (grant manual + compra via webhook): colapsa no registro de
      // compra (preserva os marcadores usados pelos triggers) ou, sem ele, no
      // grant manual — antes, o findIndex editava só o primeiro duplicado.
      const allProducts = user.productIds ? [...user.productIds] : [];
      const dupes = allProducts.filter(p => p.productId === selectedProduct);
      const others = allProducts.filter(p => p.productId !== selectedProduct);
      const keeper = dupes.find(p => p.subGuruId || p.hotmartSubscriptionCode || p.productGuruId || p.transactionIdentifier)
        || dupes[0]
        || { productId: selectedProduct };
      const isPurchase = !!(keeper.subGuruId || keeper.hotmartSubscriptionCode || keeper.productGuruId || keeper.transactionIdentifier);
      const updatedUserProducts = [
        ...others,
        // `source: 'manual'` protege o grant do painel dos fluxos de webhook.
        { ...keeper, ...(isPurchase ? {} : { source: 'manual' }), expirationDate: adjustedDate },
      ];
  
      try {
        await setDoc(userRef, { productIds: updatedUserProducts }, { merge: true });
        location.reload();
      } catch (error) {
        console.error("Erro ao atualizar produto:", error);
      }
    }
  };
  

  const toJsDate = (d) => (d instanceof Date ? d : d?.toDate?.() ?? null);

  const hasManualGrantForProduct = (productId) =>
    (userProductIds || []).some(p => p.productId === productId && isManualGrant(p));

  const data = products.map(product => {
    const isStripe = product.marketplace === 'STRIPE';
    // Mesma regra dos handlers: plano Stripe = STRIPE + type 'plan'.
    const isSubscription = isStripe && product.type === 'plan';

    // Produtos (Guru ou Pagarme): pode haver MAIS DE UM registro do mesmo
    // produto (grant manual + compra via webhook coexistem por design).
    // Usa o mais favorável — sem expiração (perpétuo) > maior validade —
    // senão um grant manual vencido mascara a compra ativa e a tela mostra
    // "Expirado"/"Sem acesso" para quem comprou de novo (ticket suporte).
    const productEntries = !isStripe && !isSubscription
      ? (userProductIds || []).filter(p => p.productId === product.productId)
      : [];
    const userProduct = productEntries.length
      ? productEntries.reduce((best, p) => {
          const bestDate = toJsDate(best.expirationDate);
          const currDate = toJsDate(p.expirationDate);
          if (!bestDate) return best;      // best é perpétuo, nada supera
          if (!currDate) return p;         // p é perpétuo
          return currDate > bestDate ? p : best;
        })
      : null;

    // Cursos Stripe (planos não entram aqui: acesso deles é o campo user.plan)
    const userCourse = isStripe && !isSubscription
      ? user?.courses?.find(c => c.productId === product.productId)
      : null;

    // Plano Stripe (campo único user.plan)
    const userPlanMatch = isSubscription && user?.plan === product.productId;

    const rawDate = userProduct?.expirationDate || userCourse?.dueDate;

    const jsDate = rawDate instanceof Date
      ? rawDate
      : rawDate?.toDate?.();

    const formattedExpirationDate = jsDate
      ? jsDate.toLocaleDateString('pt-BR', options)
      : null;

    const hasAccess = !!userProduct || !!userCourse || !!userPlanMatch;
    const isStripeCourse = isStripe && !isSubscription;
    const manualProductIds = hasManualGrantForProduct(product.productId);
    const purchaseProductIds =
      !!userProduct && isPurchaseLinkedProductEntry(userProduct);
    const manualStripeCourse =
      isStripeCourse && userCourse && isManualStripeCourseEntry(userCourse);
    const purchaseStripeCourse =
      isStripeCourse && userCourse && !isManualStripeCourseEntry(userCourse);

    const manualStripePlan =
      isSubscription &&
      isManualStripePlanAccess(user, product.productId, stripeSubscriptions);

    const canRemoveFromPanel =
      manualStripePlan ||
      (manualProductIds && !isStripe) ||
      manualStripeCourse ||
      false;

    const canEditValidity =
      (hasAccess &&
        ((manualProductIds && !isStripe) ||
          (purchaseProductIds && !!jsDate) ||
          manualStripeCourse ||
          (purchaseStripeCourse && !!jsDate))) ||
      false;

    return {
      ...product,
      hasAccess,
      status: userProduct?.status,
      expirationDate: formattedExpirationDate,
      originalExpirationDate: jsDate,
      canRemoveFromPanel,
      canEditValidity,
    };
  })
    // Plano arquivado só aparece se ESTE aluno tiver acesso a ele: a lista de
    // concessão segue limpa (não se vende plano arquivado) sem esconder as
    // assinaturas legadas que continuam ativas.
    .filter(row => !(row.archivedPlan && !row.hasAccess));


  // ────────────────────────────────────────────────────────────────────────────
  // ADMIN "Editar acesso" mode — modern, neutral admin layout.
  // Gated entirely behind editUserAccess so the student self-profile mode below is
  // visually untouched. Reuses ADMIN_COLORS / ADMIN_FONT / AdminInput / AdminAlert /
  // AdminDatePicker / formStyles. All handlers and data/columns logic are preserved.
  // ────────────────────────────────────────────────────────────────────────────
  if (editUserAccess) {
    // Marketplace normalizado (plataforma) e Tipo (Plano/Produto).
    const marketplaceLabel = (mp) =>
      mp === "STRIPE" ? "Stripe" : mp === "Guru Digital" ? "Guru" : mp === "PAGARME" ? "Pagarme" : mp === "IAP" ? "IAP" : (mp || "—");
    const tipoInfo = (row) => {
      // Planos IAP (assinaturas da loja) não têm `type`; deduzimos pela recorrência no productId.
      const isSubscription =
        row.type === "plan" ||
        (row.marketplace === "IAP" && /monthly|quarterly|semiannual|yearly/i.test(row.productId || ""));
      return { label: isSubscription ? "Assinatura" : "Produto", variant: isSubscription ? "subscription" : "product" };
    };
    // Estado do acesso considerando a EXPIRAÇÃO (não só a presença):
    // 'active' (com acesso) | 'expired' (venceu) | 'none' (sem acesso).
    const accessState = (row) => {
      if (!row.hasAccess) return 'none';
      const exp = row.originalExpirationDate; // Date ou null (perpétuo/plano)
      if (!exp) return 'active';
      return exp > new Date() ? 'active' : 'expired';
    };
    // Status geral do aluno para a tag do cabeçalho, avaliando TODOS os acessos:
    // basta 1 ativo → "Com acesso"; senão, se houver algum expirado → "Expirado";
    // sem nenhum registro → "Sem acesso".
    const overallAccess = data.some((r) => accessState(r) === 'active')
      ? 'active'
      : data.some((r) => accessState(r) === 'expired')
        ? 'expired'
        : 'none';
    const profileInitial = (Nome_Completo || Email || "?").trim().charAt(0).toUpperCase();

    const ActionText = ({ onPress, children, danger }) => (
      <Pressable onPress={onPress} style={adminStyles.linkBtn}>
        <Text style={[adminStyles.linkText, danger && { color: ADMIN_COLORS.danger }]}>
          {children}
        </Text>
      </Pressable>
    );

    const adminColumns = [
      {
        name: "Nome",
        selector: (row) => row.name,
        sortable: true,
        grow: 1.6,
        wrap: true,
      },
      {
        name: "Marketplace",
        selector: (row) => marketplaceLabel(row.marketplace),
        sortable: true,
        width: "130px",
        cell: (row) => <Badge variant="neutral">{marketplaceLabel(row.marketplace)}</Badge>,
      },
      {
        name: "Tipo",
        selector: (row) => tipoInfo(row).label,
        sortable: true,
        width: "120px",
        cell: (row) => {
          const t = tipoInfo(row);
          return <Badge variant={t.variant}>{t.label}</Badge>;
        },
      },
      {
        name: "Acesso",
        selector: (row) => {
          const s = accessState(row);
          return s === "active" ? "Com acesso" : s === "expired" ? "Expirado" : "Sem acesso";
        },
        sortable: true,
        width: "140px",
        cell: (row) => {
          const s = accessState(row);
          const map = {
            active: { variant: "success", label: "Com acesso" },
            expired: { variant: "danger", label: "Expirado" },
            none: { variant: "neutral", label: "Sem acesso" },
          };
          const c = map[s];
          return <Badge variant={c.variant}>{c.label}</Badge>;
        },
      },
      {
        name: "Validade de acesso",
        cell: (row) =>
          row.canEditValidity ? (
            <View style={adminStyles.dateCell}>
              <Text style={adminStyles.cellText}>{row.expirationDate || "—"}</Text>
              <Pressable
                onPress={() => handleDateEdit(row.productId, row.expirationDate)}
                hitSlop={6}
                style={adminStyles.iconBtn}
              >
                <Feather name="edit-3" size={16} color={ADMIN_COLORS.ink} />
              </Pressable>
            </View>
          ) : (
            <Text style={adminStyles.cellMuted}>{row.expirationDate || "—"}</Text>
          ),
      },
      {
        name: "Ações",
        width: "170px",
        cell: (row) =>
          row.canRemoveFromPanel ? (
            <ActionText danger onPress={() => handleRemoveAccess(row.productId)}>
              Remover
            </ActionText>
          ) : !row.hasAccess ? (
            <ActionText onPress={() => handleAddAccess(row.productId)}>Adicionar acesso</ActionText>
          ) : (
            <Text style={adminStyles.cellMuted}>—</Text>
          ),
      },
    ];

    const adminCustomStyles = {
      table: {
        style: {
          backgroundColor: ADMIN_COLORS.surface,
          borderRadius: "12px",
          border: `1px solid ${ADMIN_COLORS.border}`,
          overflow: "hidden",
        },
      },
      headRow: {
        style: {
          backgroundColor: ADMIN_COLORS.surfaceSubtle,
          borderBottomColor: ADMIN_COLORS.border,
          minHeight: "46px",
        },
      },
      headCells: {
        style: {
          color: ADMIN_COLORS.muted,
          fontFamily: ADMIN_FONT,
          fontSize: "11px",
          fontWeight: "700",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        },
      },
      rows: {
        style: {
          color: ADMIN_COLORS.ink,
          fontFamily: ADMIN_FONT,
          fontSize: "13px",
          minHeight: "56px",
          borderBottomColor: ADMIN_COLORS.border,
        },
        highlightOnHoverStyle: {
          backgroundColor: ADMIN_COLORS.surfaceSubtle,
          borderBottomColor: ADMIN_COLORS.border,
          outline: "none",
        },
      },
      cells: { style: { paddingTop: "8px", paddingBottom: "8px" } },
      pagination: {
        style: {
          fontFamily: ADMIN_FONT,
          color: ADMIN_COLORS.muted,
          borderTopColor: ADMIN_COLORS.border,
        },
      },
    };

    return (
      <Provider>
        <SafeAreaView style={{ flex: 1, backgroundColor: ADMIN_COLORS.surfaceSubtle }}>
          {user ? (
            <ScrollView
              style={{ flex: 1, backgroundColor: ADMIN_COLORS.surfaceSubtle }}
              contentContainerStyle={adminStyles.scrollContent}
            >
              <View style={adminStyles.page}>
                {/* Profile header */}
                <View style={adminStyles.profileHeader}>
                  <View style={adminStyles.avatar}>
                    <Text style={adminStyles.avatarText}>{profileInitial}</Text>
                  </View>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={adminStyles.profileName} numberOfLines={1}>
                      {Nome_Completo || "Aluno sem nome"}
                    </Text>
                    <Text style={adminStyles.profileEmail} numberOfLines={1}>
                      {Email || "—"}
                    </Text>
                  </View>
                  <Badge variant={overallAccess === "active" ? "success" : overallAccess === "expired" ? "danger" : "neutral"}>
                    {overallAccess === "active" ? "Com acesso" : overallAccess === "expired" ? "Expirado" : "Sem acesso"}
                  </Badge>
                </View>

                {/* Dados do aluno */}
                <SectionCard
                  title="Dados do aluno"
                  subtitle="Edite as informações de contato do aluno."
                >
                  {adminFeedback ? (
                    <AdminAlert
                      variant={adminFeedback.variant}
                      onClose={() => setAdminFeedback(null)}
                    >
                      {adminFeedback.message}
                    </AdminAlert>
                  ) : null}

                  <Field style={{ marginTop: adminFeedback ? 16 : 0 }}>
                    <FieldLabel>Email</FieldLabel>
                    <View style={adminStyles.readonly}>
                      <Text style={adminStyles.readonlyText}>{Email || "—"}</Text>
                    </View>
                  </Field>

                  <Field>
                    <FieldLabel>Nome completo</FieldLabel>
                    <AdminInput
                      placeholder="Nome"
                      value={Nome_Completo}
                      onChangeText={(value) => {
                        setUser({ ...user, Nome_Completo: value });
                        setErrorName(null);
                      }}
                    />
                  </Field>

                  <Field>
                    <FieldLabel>Celular</FieldLabel>
                    <AdminInput
                      placeholder="(DDD)99999-9999"
                      value={Celular}
                      onChangeText={(value) => {
                        setUser({ ...user, Celular: value });
                        setErrorCellphone(null);
                      }}
                    />
                  </Field>

                  <Field>
                    <FieldLabel>{CPF ? "CPF" : "Documento"}</FieldLabel>
                    <View style={adminStyles.readonly}>
                      <Text style={adminStyles.readonlyText}>{CPF || Doc || "—"}</Text>
                    </View>
                  </Field>

                  <Pressable
                    onPress={handleSave}
                    disabled={loadingSave}
                    style={({ hovered }) => [
                      adminStyles.primaryBtn,
                      hovered && { backgroundColor: ADMIN_COLORS.accentDark },
                      loadingSave && { opacity: 0.7 },
                    ]}
                  >
                    {loadingSave ? (
                      <ActivityIndicator color="#FFFFFF" size="small" />
                    ) : (
                      <Text style={adminStyles.primaryBtnText}>Salvar</Text>
                    )}
                  </Pressable>
                </SectionCard>

                {/* Acessos do aluno */}
                <SectionCard
                  title="Acessos do aluno"
                  subtitle="Conceda, edite a validade ou remova o acesso a produtos, cursos e planos."
                >
                  <DataTable
                    columns={adminColumns}
                    data={data}
                    customStyles={adminCustomStyles}
                    highlightOnHover
                    pointerOnHover
                    dense
                  />
                </SectionCard>
              </View>

              {/* Date picker modal */}
              <Modal visible={isCalendarVisible} animationType="fade" transparent>
                <View style={adminStyles.modalOverlay}>
                  <View style={adminStyles.modalCard}>
                    <Text style={adminStyles.modalTitle}>Definir validade de acesso</Text>
                    <Text style={adminStyles.modalSubtitle}>
                      Selecione a data em que o acesso expira.
                    </Text>
                    <AdminDatePicker
                      value={toISODate(newExpirationDate)}
                      minDate="2000-01-01"
                      onChange={(iso) => { setNewExpirationDate(iso); setDayStatus('ok'); }}
                    />
                    {dayStatus === 'error' && (
                      <AdminAlert variant="error">Selecione um dia no calendário para salvar.</AdminAlert>
                    )}
                    <View style={adminStyles.modalActions}>
                      <Pressable
                        onPress={() => setCalendarVisible(false)}
                        style={({ hovered }) => [
                          adminStyles.secondaryBtn,
                          hovered && { backgroundColor: ADMIN_COLORS.border },
                        ]}
                      >
                        <Text style={adminStyles.secondaryBtnText}>Cancelar</Text>
                      </Pressable>
                      <Pressable
                        onPress={() =>
                          dayStatus !== 'ok'
                            ? setDayStatus('error')
                            : handleDateConfirm(toISODate(newExpirationDate) || newExpirationDate)
                        }
                        style={({ hovered }) => [
                          adminStyles.primaryBtnSm,
                          hovered && { backgroundColor: ADMIN_COLORS.accentDark },
                        ]}
                      >
                        <Text style={adminStyles.primaryBtnText}>Salvar</Text>
                      </Pressable>
                    </View>
                  </View>
                </View>
              </Modal>
            </ScrollView>
          ) : (
            <View style={adminStyles.emptyState}>
              <Text style={adminStyles.emptyTitle}>Aluno não encontrado</Text>
              <Text style={adminStyles.emptySubtitle}>
                Não foi possível carregar os dados deste aluno.
              </Text>
            </View>
          )}
        </SafeAreaView>
      </Provider>
    );
  }


  // ── Tokens de cor/tipografia DO CLIENTE (marca) para a visão do aluno ──────
  // Usa o tema do APP (background.app / text.app / button.*App), igual à tela de
  // Favoritos e ao restante do app logado — NÃO o tema de auth (login).
  const clientBg = customColor?.background?.app || THEME.COLORS.BACKGROUND_MAIN;
  const clientText = customColor?.text?.app || THEME.COLORS.TEXT_MAIN;
  const clientPrimary = customColor?.primary || THEME.COLORS.PRIMARY_800;
  const clientBtnBg = customColor?.button?.backgroundApp || THEME.COLORS.PRIMARY_800;
  const clientBtnText = customColor?.button?.textApp || THEME.COLORS.TEXT_BUTTON;
  const cFontTitle = customFont?.fontFamilySubtitle
    ? removeSansSerif(customFont.fontFamilySubtitle)
    : THEME.FONTFAMILY.MEDIUM;
  const cFontText = customFont?.fontFamilyText
    ? removeSansSerif(customFont.fontFamilyText)
    : THEME.FONTFAMILY.REGULAR;
  // Superfícies/bordas derivadas da cor de texto do cliente → funciona em
  // qualquer tema (claro ou escuro), sem "sumir" o card no fundo.
  const cSurface = hexToRgba(clientText, 0.05) || '#FFFFFF';
  const cSurfaceStrong = hexToRgba(clientText, 0.08) || '#F3F4F6';
  const cBorder = hexToRgba(clientText, 0.15) || '#E5E7EB';
  const cMuted = hexToRgba(clientText, 0.6) || '#6B7280';
  const cAvatarBg = hexToRgba(clientPrimary, 0.16) || cSurfaceStrong;

  return (
    <Provider>
      <SafeAreaView
        style={{
          flexGrow: 1,
          backgroundColor: clientBg,
        }}
      >
        {user ? (
          <>
            {!editUserAccess && (
              <Header
                onPressEditProfile={() => {
                  navigation.navigate('Conteudo', { screen: 'Aplicativo' });
                }}
                user={user}
              />
            )}
            <ScrollView contentContainerStyle={studentStyles.scrollContent}>
              <View style={studentStyles.page}>
                {/* Cabeçalho do perfil */}
                <View style={[studentStyles.profileHeader, { backgroundColor: cSurface, borderColor: cBorder }]}>
                  <View style={[studentStyles.avatar, { backgroundColor: cAvatarBg }]}>
                    <Text style={[studentStyles.avatarText, { color: clientPrimary, fontFamily: cFontTitle }]}>
                      {(Nome_Completo || Email || '?').trim().charAt(0).toUpperCase()}
                    </Text>
                  </View>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text numberOfLines={1} style={[studentStyles.profileName, { color: clientText, fontFamily: cFontTitle }]}>
                      {Nome_Completo || 'Aluno(a)'}
                    </Text>
                    <Text numberOfLines={1} style={[studentStyles.profileEmail, { color: cMuted, fontFamily: cFontText }]}>
                      {Email}
                    </Text>
                  </View>
                </View>

                {/* Plano e compras */}
                {((user?.plan && user?.stripeId) || user?.guruId) && (
                  <View style={[studentStyles.card, { backgroundColor: cSurface, borderColor: cBorder }]}>
                    <Text style={[studentStyles.cardTitle, { color: clientText, fontFamily: cFontTitle }]}>
                      Plano e compras
                    </Text>
                    <Text style={[studentStyles.cardSubtitle, { color: cMuted, fontFamily: cFontText }]}>
                      Gerencie sua assinatura e suas compras.
                    </Text>

                    {user?.plan && user?.stripeId && (
                      <View style={{ marginTop: 12, alignSelf: 'flex-start' }}>
                        <Button
                          title={'Editar meu plano'}
                          isLoading={loadingPlan}
                          onPress={sendToCustomerPortal}
                          borderRadius="8px"
                          colorbutton={clientBtnBg}
                          colortitle={clientBtnText}
                        />
                      </View>
                    )}
                    {user?.plan && user?.stripeId && user?.plan?.length === 1 && (
                      <Text style={[studentStyles.warning, { color: cMuted, fontFamily: cFontText }]}>
                        ATENÇÃO: Ocorreu algum erro no pagamento do seu cartão ou você possui um
                        boleto pendente! Por favor, verifique seu e-mail e normalize a situação,
                        para poder retornar o acesso aos conteúdos no app.
                      </Text>
                    )}
                    {user?.guruId && (
                      <View style={{ marginTop: 12, alignSelf: 'flex-start' }}>
                        <Button
                          title={'Gerenciar minhas compras'}
                          isLoading={loadingPlan}
                          onPress={sendToCustomerPortalOfGuru}
                          borderRadius="8px"
                          colorbutton={clientBtnBg}
                          colortitle={clientBtnText}
                        />
                      </View>
                    )}
                  </View>
                )}

                {/* Editar informações */}
                <View style={[studentStyles.card, { backgroundColor: cSurface, borderColor: cBorder }]}>
                  <Text style={[studentStyles.cardTitle, { color: clientText, fontFamily: cFontTitle }]}>
                    Editar informações
                  </Text>
                  <Text style={[studentStyles.cardSubtitle, { color: cMuted, fontFamily: cFontText }]}>
                    Atualize seus dados de contato.
                  </Text>

                  <View style={studentStyles.field}>
                    <Text style={[studentStyles.label, { color: clientText, fontFamily: cFontText }]}>Email</Text>
                    <View style={[studentStyles.readonlyBox, { backgroundColor: cSurfaceStrong, borderColor: cBorder }]}>
                      <Text numberOfLines={1} style={[studentStyles.readonlyText, { color: cMuted, fontFamily: cFontText }]}>
                        {Email || '—'}
                      </Text>
                    </View>
                  </View>

                  <View style={studentStyles.field}>
                    <Text style={[studentStyles.label, { color: clientText, fontFamily: cFontText }]}>Nome completo</Text>
                    <BrandInput
                      placeholder="Nome"
                      keyboardType="default"
                      returnKeyType="go"
                      value={Nome_Completo}
                      onChangeText={(value) => {
                        setUser({ ...user, Nome_Completo: value });
                        setErrorName(null);
                      }}
                      error={!!errorName}
                    />
                    {!!errorName && (
                      <Text style={[studentStyles.errorText, { fontFamily: cFontText }]}>{errorName}</Text>
                    )}
                  </View>

                  <View style={studentStyles.field}>
                    <Text style={[studentStyles.label, { color: clientText, fontFamily: cFontText }]}>Celular</Text>
                    <BrandInput
                      placeholder="(DDD)99999-9999"
                      keyboardType="default"
                      returnKeyType="go"
                      value={Celular}
                      onChangeText={(value) => {
                        setUser({ ...user, Celular: value });
                        setErrorCellphone(null);
                      }}
                      error={!!errorCellphone}
                    />
                    {!!errorCellphone && (
                      <Text style={[studentStyles.errorText, { fontFamily: cFontText }]}>{errorCellphone}</Text>
                    )}
                  </View>

                  <View style={studentStyles.field}>
                    <Text style={[studentStyles.label, { color: clientText, fontFamily: cFontText }]}>
                      {CPF ? 'CPF' : 'Documento'}
                    </Text>
                    <View style={[studentStyles.readonlyBox, { backgroundColor: cSurfaceStrong, borderColor: cBorder }]}>
                      <Text numberOfLines={1} style={[studentStyles.readonlyText, { color: cMuted, fontFamily: cFontText }]}>
                        {CPF || Doc || '—'}
                      </Text>
                    </View>
                  </View>

                  <View style={{ marginTop: 4, alignSelf: 'flex-start' }}>
                    <Button
                      title={'SALVAR'}
                      isLoading={loadingSave}
                      onPress={handleSave}
                      borderRadius="8px"
                      colorbutton={clientBtnBg}
                      colortitle={clientBtnText}
                    />
                  </View>
                </View>
              </View>
            </ScrollView>

            {visibleAlert && (
              <AlertBox
                message={message}
                visible={visibleAlert}
                onClose={hideAlert}
              />
            )}
          </>
        ) : (
          <Container justifyContent="center">
            <StandardText
              style={{
                fontFamily:
                  customFont && customFont.fontFamilySubtitle
                    ? customFont.fontFamilySubtitle
                    : THEME.FONTFAMILY.MEDIUM,
              }}
              color={
                customColor && customColor.text && customColor.text.auth
                  ? customColor.text.auth
                  : THEME.COLORS.TEXT_ABOUT
              }
            >
              Ops, acesso negado!
            </StandardText>
            <SmallText
              style={{
                fontFamily:
                  customFont && customFont.fontFamilyText
                    ? customFont.fontFamilyText
                    : THEME.FONTFAMILY.REGULAR,
              }}
              color={
                customColor && customColor.text && customColor.text.auth
                  ? customColor.text.auth
                  : THEME.COLORS.TEXT_ABOUT
              }
              margin="2rem"
            >
              Se você já possui cadastro, clique no botão abaixo para fazer
              login com o seu email e senha:
            </SmallText>
            <Button
              title={'Login'}
              onPress={() => navigation.navigate('Login')}
              colorbutton={
                customColor &&
                  customColor.button &&
                  customColor.button.backgroundAuth
                  ? customColor.button.backgroundAuth
                  : THEME.COLORS.PRIMARY_800
              }
              colortitle={
                customColor && customColor.button && customColor.button.textAuth
                  ? customColor.button.textAuth
                  : THEME.COLORS.TEXT_BUTTON
              }
            ></Button>
          </Container>
        )}
      </SafeAreaView>
    </Provider>
  );
}

const styles = StyleSheet.create({
  modalView: {
    margin: 20,
    backgroundColor: "white",
    borderRadius: 20,
    padding: 35,
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2
    },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5
  },
});

// Neutral admin styles — used ONLY in the editUserAccess ("Editar acesso") mode.
// All colors come from ADMIN_COLORS; font is the single ADMIN_FONT token.
const adminStyles = StyleSheet.create({
  scrollContent: {
    padding: 24,
    alignItems: "center",
  },
  page: {
    width: "100%",
    maxWidth: 920,
  },
  profileHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 14,
    backgroundColor: ADMIN_COLORS.surface,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    borderRadius: ADMIN_COLORS.radius,
    padding: 20,
    marginBottom: 16,
    boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
  },
  avatar: {
    width: 52,
    height: 52,
    borderRadius: 26,
    backgroundColor: ADMIN_COLORS.tealSoft,
    alignItems: "center",
    justifyContent: "center",
  },
  avatarText: {
    fontFamily: ADMIN_FONT,
    fontSize: 20,
    fontWeight: "700",
    color: ADMIN_COLORS.teal,
  },
  profileName: {
    fontFamily: ADMIN_FONT,
    fontSize: 20,
    fontWeight: "700",
    color: ADMIN_COLORS.ink,
  },
  profileEmail: {
    fontFamily: ADMIN_FONT,
    fontSize: 14,
    color: ADMIN_COLORS.muted,
    marginTop: 2,
  },
  readonly: {
    height: 44,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    borderRadius: 10,
    paddingHorizontal: 14,
    marginTop: 6,
    justifyContent: "center",
    backgroundColor: ADMIN_COLORS.surfaceSubtle,
  },
  readonlyText: {
    fontFamily: ADMIN_FONT,
    fontSize: 15,
    color: ADMIN_COLORS.muted,
  },
  primaryBtn: {
    height: 44,
    borderRadius: 10,
    paddingHorizontal: 22,
    backgroundColor: ADMIN_COLORS.teal,
    alignItems: "center",
    justifyContent: "center",
    alignSelf: "flex-start",
    marginTop: 4,
    cursor: "pointer",
  },
  primaryBtnSm: {
    height: 40,
    borderRadius: 10,
    paddingHorizontal: 20,
    backgroundColor: ADMIN_COLORS.teal,
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  primaryBtnText: {
    fontFamily: ADMIN_FONT,
    fontSize: 15,
    fontWeight: "600",
    color: "#FFFFFF",
  },
  secondaryBtn: {
    height: 40,
    borderRadius: 10,
    paddingHorizontal: 20,
    backgroundColor: ADMIN_COLORS.secondaryBg,
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  secondaryBtnText: {
    fontFamily: ADMIN_FONT,
    fontSize: 15,
    fontWeight: "600",
    color: ADMIN_COLORS.secondaryText,
  },
  linkBtn: {
    paddingVertical: 4,
    cursor: "pointer",
  },
  linkText: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    fontWeight: "600",
    color: ADMIN_COLORS.teal,
  },
  dateCell: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  iconBtn: {
    padding: 2,
    cursor: "pointer",
  },
  cellText: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    color: ADMIN_COLORS.ink,
  },
  cellMuted: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    color: ADMIN_COLORS.placeholder,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(17,24,39,0.45)",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  modalCard: {
    width: "100%",
    maxWidth: 380,
    backgroundColor: ADMIN_COLORS.surface,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    padding: 22,
    boxShadow: "0 12px 32px rgba(16,24,40,0.18)",
  },
  modalTitle: {
    fontFamily: ADMIN_FONT,
    fontSize: 17,
    fontWeight: "700",
    color: ADMIN_COLORS.ink,
  },
  modalSubtitle: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    color: ADMIN_COLORS.muted,
    marginTop: 4,
    marginBottom: 8,
  },
  modalActions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 18,
  },
  emptyState: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  emptyTitle: {
    fontFamily: ADMIN_FONT,
    fontSize: 18,
    fontWeight: "700",
    color: ADMIN_COLORS.ink,
  },
  emptySubtitle: {
    fontFamily: ADMIN_FONT,
    fontSize: 14,
    color: ADMIN_COLORS.muted,
    marginTop: 6,
    textAlign: "center",
  },
});

// Estrutura da visão do ALUNO (mesmo layout do admin, mas as cores são aplicadas
// inline a partir da marca do cliente — aqui ficam apenas medidas/espaçamentos).
const studentStyles = StyleSheet.create({
  scrollContent: {
    flexGrow: 1,
    padding: 24,
    alignItems: "center",
  },
  page: {
    width: "100%",
    maxWidth: 640,
  },
  profileHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 14,
    borderWidth: 1,
    borderRadius: 14,
    padding: 20,
    marginBottom: 16,
    boxShadow: "0 1px 2px rgba(16,24,40,0.06)",
  },
  avatar: {
    width: 52,
    height: 52,
    borderRadius: 26,
    alignItems: "center",
    justifyContent: "center",
  },
  avatarText: {
    fontSize: 20,
    fontWeight: "700",
  },
  profileName: {
    fontSize: 20,
    fontWeight: "700",
  },
  profileEmail: {
    fontSize: 14,
    marginTop: 2,
  },
  card: {
    borderWidth: 1,
    borderRadius: 14,
    padding: 20,
    marginBottom: 16,
    boxShadow: "0 1px 2px rgba(16,24,40,0.06)",
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: "700",
  },
  cardSubtitle: {
    fontSize: 13,
    marginTop: 4,
    marginBottom: 8,
  },
  field: {
    marginBottom: 16,
  },
  label: {
    fontSize: 14,
    fontWeight: "600",
    marginBottom: 6,
  },
  // Mesma geometria do BrandInput (44 / raio 10 / borda 1 / padding 14): a
  // única diferença visual de um campo somente leitura é o fundo mais forte.
  readonlyBox: {
    minHeight: 44,
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 14,
    justifyContent: "center",
  },
  readonlyText: {
    fontSize: 15,
  },
  errorText: {
    fontSize: 12.5,
    color: THEME.COLORS.ALERT,
    marginTop: 6,
  },
  warning: {
    fontSize: 12.5,
    lineHeight: 18,
    marginTop: 10,
  },
});
